import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { finalize, Observable, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ApiResponse } from '../../../../../core/models/api-response.models';
import { ApiResponses } from '../../../../../core/models/common.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { CustomQuestionReportType, InsightMode } from '../../../insights.enums';
import {
  Attachment,
  CaseDetailModel,
  EditBehaviorDrawerParams,
} from '../../../insights.interface';
import { FrequencyDatasheetGraphComponent } from '../../insights-data/data-graph/frequency-datasheet-graph/frequency-datasheet-graph.component';
import {
  BehaviorGraphDataResponse,
  CaseBehaviorUnitMapping,
  caseBehaviorUnitMappings,
  frequencyDatasheetGraph,
  frequencyIntervalGraph,
  GraphFilterEvent,
  GraphList,
  latencyGraph,
  partialIntervalGraph,
  replacementDataType,
} from '../../insights-data/insights-data.interface';
import { InsightsDataService } from '../../insights-data/insights-data.service';
import { ManageGraphComponent } from '../../insights-data/manage-graph.component';
import { PrintBipComponent } from '../print-bip/print-bip.component';
import {
  behaviorData,
  BipPrintData,
  CaseAttachment,
  CustomQuestionAnswer,
  PlanTypeAttachment,
} from './print-bip.interface';
import { PrintBipService } from './print-bip.service';
import { BipTemplateService } from '../../../../bip-template/bip-template.service';
import { StudentAttachmentService } from '../../insights-admin/student-attachment.service';
import { TemplateModel } from '../../../../bip-template/bip-template.interface';
import { OrganizationService } from '../../../../organizations/organization.service';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { format, parse } from 'date-fns';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LoaderService } from '../../../../../core/services/loader.service';
import { forEach, groupBy, map, union } from 'lodash';
import { LatencyGraphComponent } from '../../insights-data/data-graph/latency-graph/latency-graph.component';
import { PartialIntervalGraphComponent } from '../../insights-data/data-graph/partial-interval-graph/partial-interval-graph.component';
import { ReplacementBehaviorGraphComponent } from '../../insights-data/data-graph/replacement-behavior-graph/replacement-behavior-graph.component';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { CustomQuestionService } from '../../../../manage-settings/custom-question/custom-question.service';
import { QuestionItem } from '../../../../manage-settings/manage-settings.interface';
import { FrequencyIntervalGraphComponent } from '../../insights-data/data-graph/frequency-interval-graph/frequency-interval-graph.component';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { FeedbackService } from '../../feedback/feedback.service';
import { FeedbackComponent } from '../../feedback/feedback.component';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';
import { GraphFilterComponent } from '../../insights-data/data-graph/graph-filter/graph-filter.component';
import { DATE_FORMAT } from '../../feedback/feeback.enum';
import { AuditLogsService } from '../../../../audit-logs/audit-logs.service';
import {
  AuditModule,
  AuditOperationType,
} from '../../../../audit-logs/audit-logs.constants';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { GraphFilterViewTypeOptions } from '../../insights-data/data-graph/graph-filter/graph-filter.enum';

@Component({
  selector: 'app-print-options',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzCheckboxModule,
    NzFormModule,
    NzFormItemComponent,
    NzCheckboxModule,
    NzDividerModule,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzDatePickerModule,
    NzInputModule,
    NzTableModule,
    NzButtonModule,
    FormsModule,
    ReactiveFormsModule,
    DatePipe,
    CommonModule,
    NzSpinModule,
    NzSelectModule,
    NzModalModule,
    UserDatePipe,
    GraphFilterComponent,
  ],
  templateUrl: './print-options.component.html',
  styleUrl: './print-options.component.scss',
})
export class PrintOptionsComponent
  extends ManageGraphComponent
  implements OnInit, OnDestroy
{
  @ViewChild('feedbackDataTpl') feedbackDataTpl!: TemplateRef<void>;
  loading$: Observable<boolean>;
  caseParams: EditBehaviorDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.PRINT_CASE,
  };
  caseData = {} as BipPrintData;
  graphList: GraphList[] = [];
  organizationDetail?: OrganizationModel;
  bipTemplate?: TemplateModel | null;
  PDF_MEDIA_TYPE = 1;
  checkedAttachments: CaseAttachment[] = [];
  checkedGraphList: GraphList[] = [];
  customQuestionList: QuestionItem[] = [];
  datasheetData: { id: number; value: string }[] = [
    { id: 1, value: 'Frequency Based Datasheet' },
    { id: 2, value: 'Latency' },
    { id: 3, value: 'Partial Interval' },
    { id: 4, value: 'Replacement Behavior' },
  ];
  templateSections = [
    { id: 1, sectionName: 'Challenging Behavior' },
    { id: 2, sectionName: 'Strategy Details' },
    { id: 3, sectionName: 'Custom Questions' },
    { id: 4, sectionName: 'Reports' },
  ];
  isPrintLoading = false;
  printOptionsForm!: FormGroup;
  printDraft = false;
  addGraphToReports = false;
  startDate?: Date;
  endDate?: Date;
  isFeedbackSubmitted$: Observable<boolean>;
  isModalVisible = false;
  isAddGraph = false;
  isAddComments = false;
  caseAttachments: PlanTypeAttachment[] = [];
  activePlanAttachments: PlanTypeAttachment[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dataService: InsightsDataService,
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly PrintBipService: PrintBipService,
    private readonly templateService: BipTemplateService,
    private readonly studentAttachmentService: StudentAttachmentService,
    private readonly customQuestionService: CustomQuestionService,
    private readonly orgService: OrganizationService,
    private readonly loaderService: LoaderService,
    private readonly injector: Injector,
    private readonly fb: FormBuilder,
    private readonly feedbackService: FeedbackService,
    private readonly auditLogsService: AuditLogsService,
    private readonly auditContextService: AuditContextService,
    cd: ChangeDetectorRef
  ) {
    super(cd);
    this.loading$ = this.loaderService.loading$;
    this.isFeedbackSubmitted$ = this.feedbackService.feedbackSubmissionState$;
  }

  ngOnInit(): void {
    this.initForm();
    this.setupDrawerSubscription();
  }

  initForm() {
    this.printOptionsForm = this.fb.group({
      comment: [null],
      isDraft: [false],
      isIncludeStrategyGuidelines: [false],
      frequencyInterval: [false],
      isGraph: [false],
      isComments: [false],
      startDate: [null],
      endDate: [null],
      viewType: [1],
      isTrendLineEnabled: [false],
    });
  }

  setupDrawerSubscription() {
    this.drawerService
      .getParams<EditBehaviorDrawerParams>(DRAWER_IDS.BIP_PRINT_OPTIONS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.getMasterData();
        }
      });
  }

  async getMasterData() {
    this.loaderService.show();
    try {
      const [templateData, attchmentRes, orgDetail, queResponse] =
        await Promise.all([
          this.templateService
            .getTemplateById(this.caseParams.case.bipTemplateId)
            .toPromise(),
          this.PrintBipService.getCaseAttachmentsByPlanTypeAndCaseId(
            this.caseParams.case.id
          ).toPromise(),
          this.orgService
            .getOrganization(this.caseParams?.case?.student.organizationId)
            .toPromise(),
          this.customQuestionService
            .getQuestionsByOrg(this.caseParams?.case?.student.organizationId)
            .toPromise(),
        ]);
      this.caseAttachments = attchmentRes?.result?.data || [];
      this.bipTemplate = templateData ? templateData?.result : null;

      if (this.bipTemplate) {
        this.printOptionsForm.patchValue({
          isIncludeStrategyGuidelines:
            this.bipTemplate.isIncludeStrategyGuidelines,
        });
      }
      this.activePlanAttachments = this.caseAttachments
        .map(attachment => {
          if (attachment.planTypeId === 0) {
            return { ...attachment, sequenceNumber: 0 };
          }
          const matchingSection = this.bipTemplate?.templateSections?.find(
            section =>
              section.sectionName === attachment.planTypeName &&
              section.isSelected === true
          );
          return matchingSection
            ? { ...attachment, sequenceNumber: matchingSection.sequenceNumber }
            : null;
        })
        .filter(attachment => attachment !== null)
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      this.organizationDetail = orgDetail;
      this.customQuestionList = queResponse ? queResponse?.result : [];
      this.getBehaviorReportDetails();
    } catch (error) {
      console.error('Error loading master data:', error);
      this.commonService.notify('error', 'Failed to load data');
      this.loaderService.hide();
    }
  }

  onFilterChange(event: GraphFilterEvent) {
    this.printOptionsForm.patchValue({
      viewType: event.viewType,
      isTrendLineEnabled: event.isTrendLineEnabled,
      startDate: parse(event.startDate, DATE_FORMAT, new Date()),
      endDate: parse(event.endDate, DATE_FORMAT, new Date()),
    });
    this.validateDates('startDate', 'endDate');
  }

  validateDates(ctrl1: string, ctrl2: string) {
    const startDate = this.printOptionsForm.get(ctrl1)?.value;
    const endDate = this.printOptionsForm.get(ctrl2)?.value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        this.printOptionsForm.get(ctrl1)?.setErrors({ dateInvalid: true });
        this.printOptionsForm.get(ctrl2)?.setErrors({ dateInvalid: true });
      } else {
        this.printOptionsForm.get(ctrl1)?.setErrors(null);
        this.printOptionsForm.get(ctrl2)?.setErrors(null);
      }
    }

    this.printOptionsForm.get('isGraph')?.setValue(false);
    this.printOptionsForm.get('isComments')?.setValue(false);
    this.resetGraphs();
  }

  getBehaviorReportDetails() {
    this.loaderService.show();
    this.PrintBipService.getPrintBipData(this.caseParams.case.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponse<BipPrintData>) => {
          if (res.result) {
            this.caseData = res.result;
            this.processBehaviorData();
            this.processBipTemplateBehaviors();
            this.cd.detectChanges();
          }
          this.loaderService.hide();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
          this.loaderService.hide();
        },
      });
  }

  private processBehaviorData(): void {
    for (const behavior of this.caseData.caseBehaviors) {
      this.mapBehaviorFields(behavior);
      this.processCustomQuestions(behavior);
    }
  }

  private mapBehaviorFields(behavior: behaviorData): void {
    behavior['caseBehaviorTypeIds'] = behavior.caseBehaviorTypes.map(
      t => t.behaviorTypeName
    );
    behavior['caseBehaviorTypeDescriptionIds'] =
      behavior.caseBehaviorTypeDescriptions.map(t => t.description);
    behavior['caseBehaviorEnvironmentIds'] =
      behavior.caseBehaviorEnvironments.map(t => t.behaviorEnvironmentName);
    behavior['caseBehaviorSettingEventIds'] =
      behavior.caseBehaviorSettingEvents.map(t => t.behaviorSettingEventName);
    behavior['caseBehaviorAntecedentIds'] =
      behavior.caseBehaviorAntecedents.map(t => t.behaviorAntecedentName);
    behavior['caseBehaviorConsequenceIds'] =
      behavior.caseBehaviorConsequences.map(t => t.behaviorConsequenceName);
    behavior['caseBehaviorConsequenceDescriptionIds'] =
      behavior.caseBehaviorConsequenceDescriptions.map(t => t.description);
  }

  private processCustomQuestions(behavior: behaviorData): void {
    if (this.caseData.bipTemplateId === 1) {
      this.processDefaultTemplate(behavior);
    } else {
      this.processCustomTemplate(behavior);
    }
  }

  private processDefaultTemplate(behavior: behaviorData): void {
    behavior.caseBehvaiorCustomQuestionIds = this.customQuestionList
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .filter(this.isValidCustomQuestion.bind(this))
      .map(question => this.mapCustomQuestionWithAnswer(question, behavior));
  }

  private processCustomTemplate(behavior: behaviorData): void {
    const resultList = this.bipTemplate?.templateQuestionIds
      .filter(id => this.isQuestionNotInBehaviorQuestionaries(id))
      .map(id => this.buildCustomQuestionAnswer(id, behavior))
      .filter((q): q is CustomQuestionAnswer => q !== null);

    behavior['caseBehvaiorCustomQuestionIds'] = resultList || [];
  }

  private isValidCustomQuestion(question: QuestionItem): boolean {
    return (
      question.statusId === 1 &&
      question.questionForReportTypeId !== CustomQuestionReportType.Details
    );
  }

  private mapCustomQuestionWithAnswer(
    question: QuestionItem,
    behavior: behaviorData
  ): CustomQuestionAnswer {
    const matchedAnswer = behavior.caseBehaviorCustomQuestionAnswer.find(
      answer => answer.customQuestionId === question.id
    );
    return {
      ...question,
      answer: matchedAnswer ? matchedAnswer.answer : '',
    };
  }

  private isQuestionNotInBehaviorQuestionaries(questionId: number): boolean {
    return !this.bipTemplate?.behaviorQuestionaries.some(
      q => q.questionId === questionId
    );
  }

  private buildCustomQuestionAnswer(
    questionId: number,
    behavior: behaviorData
  ): CustomQuestionAnswer | null {
    const matchedQuestion = this.customQuestionList.find(
      q =>
        q.id === questionId &&
        q.statusId === 1 &&
        q.questionForReportTypeId !== CustomQuestionReportType.Details
    );

    if (!matchedQuestion) return null;

    const matchedAnswer = behavior.caseBehaviorCustomQuestionAnswer.find(
      q => q.customQuestionId === questionId
    );

    return {
      id: questionId,
      customQuestion: matchedQuestion.customQuestion,
      answer: matchedAnswer ? matchedAnswer.answer : '',
    };
  }

  private processBipTemplateBehaviors(): void {
    this.caseData.bipTemplateChallengingBehaviors
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .forEach(behavior => {
        if (Array.isArray(behavior.questionIds)) {
          behavior.questionIds.sort(
            (a, b) => a.sequenceNumber - b.sequenceNumber
          );
        }
      });
  }

  getGraphs() {
    this.loaderService.show();
    this.dataService
      .getGraphList(this.caseParams.case.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (res: ApiResponses<GraphList>) => {
          if (res.result) {
            this.graphList = res.result;
            this.checkedGraphList = this.graphList;
            this.cd.detectChanges();
          }
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  onOptionsCheckboxChange(event: boolean) {
    if (event) {
      this.isAddGraph = true;
      this.getGraphs();
    } else {
      this.graphList = [];
      this.resetGraphs();
      this.isAddGraph = false;
    }
  }

  onCommentCheckboxChange(event: boolean) {
    this.isAddComments = event;
  }

  resetGraphs() {
    this.checkedGraphList = [];
    this.graphList = [];
    forEach(this.caseData.caseBehaviors, behavior => {
      behavior['graphImages'] = [];
      behavior['comments'] = [];
    });
  }

  onGraphCheckboxChange(event: boolean, data: GraphList) {
    forEach(this.caseData.caseBehaviors, behavior => {
      behavior['graphImages'] = [];
      behavior['comments'] = [];
    });
    if (event) {
      this.checkedGraphList.push(data);
    } else {
      const index = this.checkedGraphList.findIndex(
        item => item.id === data.id
      );
      this.checkedGraphList.splice(index, 1);
      this.displayNoData = true;
      this.cd.detectChanges();
    }
  }

  onAttachmentCheckboxChange(event: boolean, data: CaseAttachment) {
    if (event) {
      if (data.fileUrl !== '') {
        this.checkedAttachments.push(data);
      }
    } else {
      const index = this.checkedAttachments.findIndex(
        item => item.id === data.id
      );
      if (index !== -1) {
        this.checkedAttachments.splice(index, 1);
      }
    }
  }

  isPdfFile(fileName: string): boolean {
    if (!fileName) return false;
    return fileName.toLowerCase().endsWith('.pdf');
  }

  getFilteredPdfAttachments(attachments: CaseAttachment[]): CaseAttachment[] {
    if (!attachments || !Array.isArray(attachments)) {
      return [];
    }
    return attachments.filter(
      attachment =>
        attachment.fileUrl !== '' &&
        (this.isPdfFile(attachment.name) ||
          attachment.mediaTypeId === this.PDF_MEDIA_TYPE)
    );
  }

  getGraphData() {
    const caseBehaviorUnitMappings: caseBehaviorUnitMappings[] = [];
    const startDate = this.printOptionsForm.get('startDate')?.value;
    const endDate = this.printOptionsForm.get('endDate')?.value;
    forEach(this.caseData.caseBehaviors, behavior => {
      behavior['graphImages'] = [];
      behavior['comments'] = [];
    });
    this.checkedGraphList.forEach(graph => {
      const selectedUnit = this.datasheetData.find(
        d => d.value === graph.unitName
      );
      caseBehaviorUnitMappings.push({
        caseBehaviorId: graph.caseBehaviorId,
        unitName: selectedUnit?.id || 0,
        graphType: selectedUnit?.id === 1 ? 1 : 0,
      });
      if (
        selectedUnit?.id === 1 &&
        this.printOptionsForm.get('frequencyInterval')?.value === true
      ) {
        caseBehaviorUnitMappings.push({
          caseBehaviorId: graph.caseBehaviorId,
          unitName: selectedUnit?.id || 0,
          graphType: 2,
        });
      }
    });
    if (caseBehaviorUnitMappings.length > 0) {
      this.loaderService.show();
      this.dataService
        .getMultipleGraphData({
          caseBehaviorUnitMappings,
          startDate: format(startDate, DATE_FORMAT),
          endDate: format(endDate, DATE_FORMAT),
          viewType: this.printOptionsForm.get('viewType')?.value || 1,
        })
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: async res => {
            if (res.result && res.result.caseBehaviorUnitMappings.length > 0) {
              await this.prepareGraphResponseData(
                res.result.caseBehaviorUnitMappings
              );
            }
            await this.printReport();
            this.loaderService.hide();
          },
          error: (error: Error) => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    } else {
      this.printReport();
    }
  }

  async prepareGraphResponseData(
    caseBehaviorUnitMapping: CaseBehaviorUnitMapping[]
  ): Promise<void> {
    const promises = caseBehaviorUnitMapping.map(element => {
      return new Promise<void>(resolve => {
        if (!element) {
          this.displayNoData = true;
          return resolve();
        }

        const selectedUnit = this.datasheetData.find(
          d => d.value === element.unitName
        );

        if (!selectedUnit) {
          this.displayNoData = true;
          return resolve();
        }

        this.manageGraphResponseData(element, selectedUnit)
          .then(() => resolve())
          .catch(() => resolve());
      });
    });

    await Promise.all(promises);
  }

  async manageGraphResponseData(
    caseBehaviorUnitMapping: CaseBehaviorUnitMapping,
    selectedUnit: { id: number; value: string }
  ): Promise<void> {
    try {
      const caseBehaviorId = caseBehaviorUnitMapping.caseBehaviorId;
      const graphType = caseBehaviorUnitMapping.graphType;
      const data = caseBehaviorUnitMapping.graph;
      const mappedData: BehaviorGraphDataResponse = {
        caseBehaviorId: caseBehaviorId,
        unitName: selectedUnit.value,
        partialInterval:
          selectedUnit.id === 3 ? (data as partialIntervalGraph) : null,
        frequencyDuration:
          selectedUnit.id === 1 && graphType === 1
            ? (data as frequencyDatasheetGraph)
            : null,
        frequencyInterval:
          selectedUnit.id === 1 && graphType === 2
            ? (data as frequencyIntervalGraph)
            : null,
        latency: selectedUnit.id === 2 ? (data as latencyGraph) : null,
        replacementBehavior:
          selectedUnit.id === 4 ? (data as replacementDataType) : null,
      };

      const success = await this.processBehaviorGraphData(
        mappedData,
        selectedUnit.id || 0,
        graphType,
        this.printOptionsForm.get('viewType')?.value || 1,
        this.printOptionsForm.get('isTrendLineEnabled')?.value || false,
        async (unit, graphData, showInterval) => {
          await this.generateGraphImageForBehavior(
            unit,
            caseBehaviorUnitMapping.id,
            graphData,
            caseBehaviorId,
            showInterval || false
          );
        }
      );

      if (!success) {
        this.isGraphDataAvailable = false;
        this.displayNoData = true;
      }
      const caseBehavior = this.caseData.caseBehaviors.find(
        b => b.id === caseBehaviorId
      );
      if (
        caseBehavior &&
        data &&
        data.comments &&
        data.comments.length > 0 &&
        this.isAddComments &&
        graphType !== 2
      ) {
        data.comments.forEach(comment => {
          comment.dataUnitId = caseBehaviorUnitMapping.id;
        });
        caseBehavior['comments'] = [
          ...(caseBehavior['comments'] || []),
          ...data.comments,
        ];
      }
    } catch (error) {
      console.error('Error in graph data processing:', error);
      this.commonService.notify('error', 'Error processing graph data');
    }
  }

  private async generateGraphImageForBehavior(
    selectedUnit: number,
    selectedGraph: number,
    data:
      | frequencyDatasheetGraph
      | latencyGraph
      | partialIntervalGraph
      | replacementDataType
      | frequencyIntervalGraph,
    caseBehaviorId: number,
    showIntervalFrequency = false
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      type GraphComponent =
        | FrequencyIntervalGraphComponent
        | FrequencyDatasheetGraphComponent
        | LatencyGraphComponent
        | PartialIntervalGraphComponent
        | ReplacementBehaviorGraphComponent;

      type GraphComponentType = Type<GraphComponent>;

      let componentFactory: GraphComponentType;

      switch (selectedUnit) {
        case 1:
          componentFactory = (
            showIntervalFrequency
              ? FrequencyIntervalGraphComponent
              : FrequencyDatasheetGraphComponent
          ) as GraphComponentType;
          break;
        case 2:
          componentFactory = LatencyGraphComponent as GraphComponentType;
          break;
        case 3:
          componentFactory =
            PartialIntervalGraphComponent as GraphComponentType;
          break;
        case 4:
          componentFactory =
            ReplacementBehaviorGraphComponent as GraphComponentType;
          break;
        default:
          resolve();
          return;
      }

      try {
        const factory = this.viewContainerRef.createComponent(
          componentFactory,
          {
            injector: this.injector,
          }
        );

        const componentElement = factory.location.nativeElement;
        componentElement.style.position = 'fixed';
        componentElement.style.left = '-10000px';
        componentElement.style.top = '0';
        componentElement.style.visibility = 'hidden';
        componentElement.style.pointerEvents = 'none';

        const caseBehavior = this.caseData.caseBehaviors.find(
          b => b.id === caseBehaviorId
        );

        let imagesResolved = 0;
        const totalImages = selectedUnit === 3 ? 2 : 1;
        let isSettled = false;

        const timeoutId = globalThis.setTimeout(() => {
          if (isSettled) {
            return;
          }

          isSettled = true;
          factory.destroy();
          resolve();
        }, 7000);

        const checkResolve = () => {
          if (isSettled) {
            return;
          }

          imagesResolved++;
          if (imagesResolved === totalImages) {
            isSettled = true;
            globalThis.clearTimeout(timeoutId);
            factory.destroy();
            resolve();
          }
        };

        const handleError = (error: unknown): void => {
          if (isSettled) {
            return;
          }

          isSettled = true;
          globalThis.clearTimeout(timeoutId);
          console.error('Error in graph image generation:', error);
          factory.destroy();
          reject(error);
        };

        if (selectedUnit === 3) {
          (factory.instance as PartialIntervalGraphComponent).allChartTotalImage
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (images: string[]) => {
                this.appendGraphImages(caseBehavior, images, selectedGraph);
                checkResolve();
              },
              error: handleError,
            });
        }

        factory.instance.allChartImage
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (images: string[]) => {
              this.appendGraphImages(caseBehavior, images, selectedGraph);
              checkResolve();
            },
            error: handleError,
          });

        factory.instance.graphData = data;
        factory.changeDetectorRef.detectChanges();
      } catch (error) {
        console.error('Error creating graph component:', error);
        reject(error);
      }
    });
  }

  private appendGraphImages(
    caseBehavior: behaviorData | undefined,
    images: string[],
    selectedGraph: number
  ): void {
    if (!caseBehavior || images.length === 0) {
      return;
    }

    const graphImages = images.map(img => ({
      graph: img,
      unitId: selectedGraph,
    }));

    caseBehavior['graphImages'] = [
      ...(caseBehavior['graphImages'] || []),
      ...graphImages,
    ];
  }

  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.BIP_PRINT_OPTIONS);
  }

  onCancel() {
    this.checkedAttachments = [];
    this.checkedGraphList = [];
    this.printOptionsForm.reset();
    this.closeDrawer();
  }

  groupGraphsAndComments(behavior: behaviorData) {
    const graphGroups = groupBy(behavior.graphImages, g => g.unitId.toString());
    const commentGroups = groupBy(behavior.comments, c =>
      c.dataUnitId.toString()
    );
    const allUnitIds = union(
      Object.keys(graphGroups),
      Object.keys(commentGroups)
    );
    return map(allUnitIds, unitIdStr => {
      const unitId = Number(unitIdStr);
      const commentsByDate = groupBy(
        commentGroups[unitIdStr] || [],
        c => c.dataDate
      );
      const commentsData = map(
        Object.keys(commentsByDate),
        (dateKey, index) => ({
          dataDate: dateKey,
          id: index,
          comments: commentsByDate[dateKey].map(c => ({
            id: c.commentId,
            comment: c.commentText,
            createdBy: c.createdBy,
            createdOn: c.createdOn,
            createdByName: c.createdByName,
          })),
        })
      );
      return {
        unitId: Number(unitId),
        graphs: map(graphGroups[unitIdStr] || [], g => ({
          graph: g.graph,
          unitId: g.unitId,
        })),
        commentsData: commentsData,
      };
    });
  }

  async printReport() {
    const factory = this.viewContainerRef.createComponent(PrintBipComponent);
    const componentElement = factory.location.nativeElement;
    componentElement.style.display = 'none';
    this.loaderService.show();

    try {
      const studentAge = this.caseData.student?.dateOfBirth
        ? this.PrintBipService.calculateAge(this.caseData.student.dateOfBirth)
        : null;
      this.caseData.age = studentAge;

      if (this.bipTemplate?.templateSections) {
        this.bipTemplate.templateSections =
          this.bipTemplate.templateSections.filter(section => {
            const matchingSection = this.templateSections.find(
              templateSection =>
                templateSection.sectionName === section.sectionName
            );
            if (matchingSection) {
              section.id = matchingSection.id;
              return section.isSelected !== false;
            }
            return false;
          });
      }

      if (this.checkedGraphList.length > 0) {
        for (const element of this.caseData.caseBehaviors) {
          const res = this.groupGraphsAndComments(element);
          element['groupedGraphsAndComments'] = res;
        }
      }

      this.checkedAttachments = this.checkedAttachments.sort((a, b) => {
        const attachmentA = this.activePlanAttachments.find(
          planAttachment => planAttachment.planTypeId === a.planTypeId
        );
        const attachmentB = this.activePlanAttachments.find(
          planAttachment => planAttachment.planTypeId === b.planTypeId
        );

        const sequenceA = attachmentA?.sequenceNumber ?? -1;
        const sequenceB = attachmentB?.sequenceNumber ?? -1;

        if (sequenceA === -1 && sequenceB !== -1) return -1;
        if (sequenceA !== -1 && sequenceB === -1) return 1;

        return sequenceA - sequenceB;
      });

      factory.instance.printOptions = {
        organizationDetail: this.organizationDetail,
        caseData: this.caseData,
        bipTemplate: this.bipTemplate,
        logo: null,
        graphData: null,
        attachments: this.checkedAttachments.map(
          (attachment: Attachment) => attachment.fileUrl
        ),
        PrintOptions: this.printOptionsForm.value,
      };

      this.cd.detectChanges();

      await this.waitForInitialization(factory);
      await factory.instance.printPDF();
      factory.destroy();
      this.loaderService.hide();

      this.logPrintAudit()
        .pipe(finalize(() => this.onCancel()))
        .subscribe({
          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
          },
        });
    } catch (error) {
      factory.destroy();
      this.loaderService.hide();
      this.logPrintFailureAudit().subscribe();
      this.commonService.notify('error', 'Error generating PDF');
      console.error('Error generating PDF:', error);
    }
  }

  waitForInitialization(factory: ComponentRef<PrintBipComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  private logPrintAudit(): Observable<ApiResponse<string> | null> {
    const firstName = this.caseData.student?.firstName || '';
    const lastName = this.caseData.student?.lastName || '';
    const studentName = `${firstName} ${lastName}`.trim() || 'Unknown Student';
    const _modifiedFields = this.buildPrintModifiedFields();

    return this.auditLogsService
      .logAudit({
        auditModule: AuditModule.Case,
        auditOperationType: AuditOperationType.Printed,
        summary: `Behavior Intervention Plan is printed for student ${studentName}`,
        entityType: 'CaseBIP Print',
        caseId: this.caseParams.case?.id,
        _modifiedFields,
      })
      .pipe(takeUntil(this.destroy$));
  }

  private logPrintFailureAudit(): Observable<ApiResponse<string> | null> {
    const firstName = this.caseData.student?.firstName || '';
    const lastName = this.caseData.student?.lastName || '';
    const studentName = `${firstName} ${lastName}`.trim() || 'Unknown Student';

    return this.auditLogsService
      .logAudit({
        auditModule: AuditModule.Case,
        auditOperationType: AuditOperationType.Printed,
        summary: `Behavior Intervention Plan print failed for student ${studentName}.`,
        entityType: 'CaseBIP Print',
        caseId: this.caseParams.case?.id,
      })
      .pipe(takeUntil(this.destroy$));
  }

  private buildPrintModifiedFields() {
    const formValue = this.printOptionsForm.value as Record<string, unknown>;
    const labelMap: Record<string, string> = {
      comment: 'Comments',
      isDraft: 'Draft Watermark',
      isIncludeStrategyGuidelines: 'Include Strategy Guidelines',
      frequencyInterval: 'Frequency Interval',
      isGraph: 'Include Graph',
      isComments: 'Include Graph Comments',
      startDate: 'Start Date',
      endDate: 'End Date',
      viewType: 'View Type',
      isTrendLineEnabled: 'Trend Line Enabled',
    };

    const modifiedFields = this.auditContextService.buildFieldChanges(
      {},
      formValue,
      {
        sensitiveFields: [],
        labelMap,
      }
    );

    return modifiedFields.map(field => {
      if (field.field !== 'viewType') {
        return field;
      }

      const getViewTypeLabel = (id: unknown): string => {
        if (typeof id === 'number' || typeof id === 'string') {
          const found = GraphFilterViewTypeOptions.find(
            opt => String(opt.value) === String(id)
          );
          return found ? found.label : String(id);
        }
        return '';
      };

      return {
        ...field,
        oldValue: getViewTypeLabel(field.oldValue),
        newValue: getViewTypeLabel(field.newValue),
      };
    });
  }

  handleModalCancel(): void {
    this.onCancel();
    this.isModalVisible = false;
  }

  handleModalConfirm(): void {
    this.drawerService.openDrawer(
      FeedbackComponent,
      this.feedbackDataTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        student: this.caseParams?.case.student,
        supervisorName: this.caseParams.case?.supervisorName,
        caseId: this.caseParams.case?.id,
      },
      DRAWER_IDS.CASE_FEEDBACK
    );
    this.isModalVisible = false;
  }

  closefeedbackFormDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.CASE_FEEDBACK);
    this.isModalVisible = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
